import { useEffect, useRef } from 'react';

import { useAtomsToastManager } from '@opendoor/atoms/toast';

interface CopyLinkToastProps {
  /** Rising edge of this flag surfaces the toast once. */
  open: boolean;
  message: string;
  /** Called after the toast is shown so the parent can reset its flag. */
  onShown: () => void;
  durationMs?: number;
}

/**
 * Bridges a boolean "show toast" flag to the atoms toast manager. Must render
 * inside a `<Toasty>` ancestor. Replaces the bricks-next controlled `Toast`.
 */
export const CopyLinkToast = ({
  open,
  message,
  onShown,
  durationMs = 1000,
}: CopyLinkToastProps) => {
  const toastManager = useAtomsToastManager();
  // `useAtomsToastManager` returns a fresh object each render and `add()` re-renders
  // this subtree, so read it (and the callback) through refs and key the effect on `open`.
  const toastManagerRef = useRef(toastManager);
  toastManagerRef.current = toastManager;
  const onShownRef = useRef(onShown);
  onShownRef.current = onShown;

  useEffect(() => {
    if (!open) return;
    toastManagerRef.current.add({ title: message, variant: 'success', timeout: durationMs });
    onShownRef.current();
  }, [open, message, durationMs]);

  return null;
};
